import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { Lock, Loader2, CheckCircle, AlertCircle } from 'lucide-react';

export const ResetPassword: React.FC = () => {
  const [canReset, setCanReset] = useState(false);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    let subscription: { unsubscribe: () => void } | null = null;
    let isHandled = false;

    const checkRecoveryParams = async () => {
      try {
        // Debug: log URL info
        console.log('ResetPassword - URL:', window.location.href);
        console.log('ResetPassword - Search:', window.location.search);
        console.log('ResetPassword - Hash:', window.location.hash);

        const search = new URLSearchParams(window.location.search);
        const hash = window.location.hash.startsWith('#') ? window.location.hash.substring(1) : window.location.hash;
        const hashParams = new URLSearchParams(hash);

        // Check if there's an error in URL (e.g., expired token)
        const errorParam = search.get('error') || hashParams.get('error');
        const errorDescription = search.get('error_description') || hashParams.get('error_description');
        if (errorParam) {
          console.log('ResetPassword - Error in URL:', errorParam, errorDescription);
          throw new Error(errorDescription || 'El enlace de restablecimiento no es válido.');
        }

        // Set up auth state listener to catch PASSWORD_RECOVERY event
        const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
          console.log('ResetPassword - Auth state change:', event, session ? 'has session' : 'no session');
          if (isHandled) return;
          if (event === 'PASSWORD_RECOVERY') {
            console.log('ResetPassword - PASSWORD_RECOVERY event received');
            isHandled = true;
            setCanReset(true);
            setInitializing(false);
          } else if (event === 'SIGNED_IN' && session) {
            console.log('ResetPassword - SIGNED_IN event, allowing reset');
            isHandled = true;
            setCanReset(true);
            setInitializing(false);
          }
        });
        subscription = authListener.subscription;

        // Check for hash fragment with access_token (implicit flow from email)
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = hashParams.get('type');
        
        if (accessToken && refreshToken) {
          console.log('ResetPassword - Found tokens in hash, setting session...');
          const { error: sessionError } = await supabase.auth.setSession({
            access_token: accessToken,
            refresh_token: refreshToken,
          });
          if (sessionError) {
            console.error('ResetPassword - Error setting session from tokens:', sessionError);
            throw sessionError;
          }
          console.log('ResetPassword - Session set from hash tokens');
          isHandled = true;
          setCanReset(true);
          setInitializing(false);
          return;
        }

        // Check for type=recovery in hash (Supabase might handle it automatically)
        if (type === 'recovery') {
          console.log('ResetPassword - Found recovery type in hash, waiting for Supabase...');
          await new Promise(resolve => setTimeout(resolve, 1000));
          const { data: { session } } = await supabase.auth.getSession();
          if (session) {
            console.log('ResetPassword - Session established from recovery hash');
            isHandled = true;
            setCanReset(true);
            setInitializing(false);
            return;
          }
        }

        // Check for authorization code (PKCE flow)
        const code = search.get('code');
        if (code) {
          console.log('ResetPassword - Found code in URL, attempting exchange...');
          // First check if we already have a session (detectSessionInUrl might have worked)
          const { data: { session: existingSession } } = await supabase.auth.getSession();
          if (existingSession) {
            console.log('ResetPassword - Session already exists, skipping code exchange');
            isHandled = true;
            setCanReset(true);
            setInitializing(false);
            return;
          }
          
          // Try to exchange the code
          const { error: exchangeError } = await supabase.auth.exchangeCodeForSession(code);
          if (exchangeError) {
            console.error('ResetPassword - Code exchange error:', exchangeError.message);
            // If it's a PKCE verifier error, show a helpful message
            if (exchangeError.message.includes('code verifier')) {
              throw new Error('El enlace debe abrirse en el mismo navegador donde solicitaste el cambio de contraseña. Por favor, solicitá un nuevo enlace.');
            }
            throw exchangeError;
          }
          console.log('ResetPassword - Code exchanged successfully');
          isHandled = true;
          setCanReset(true);
          setInitializing(false);
          return;
        }

        // Final check: see if there's already a session
        await new Promise(resolve => setTimeout(resolve, 500));
        const { data: { session } } = await supabase.auth.getSession();
        console.log('ResetPassword - Final session check:', session ? 'exists' : 'none');
        if (session) {
          isHandled = true;
          setCanReset(true);
          setInitializing(false);
          return;
        }
        
        // If we reach here with no session and no code/tokens, the link is invalid
        if (!isHandled) {
          setInitializing(false);
        }
      } catch (err) {
        console.error('Error inicializando restablecimiento:', err);
        const errorMessage = err instanceof Error ? err.message : 'El enlace de restablecimiento no es válido o expiró. Solicitá uno nuevo.';
        setError(errorMessage);
        setInitializing(false);
      }
    };

    checkRecoveryParams();

    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  const handleUpdatePassword = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setMessage(null);

    if (password.length < 8) {
      setError('La contraseña debe tener al menos 8 caracteres.');
      return;
    }

    if (password !== confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }

    setLoading(true);
    try {
      const { error } = await supabase.auth.updateUser({ password });
      if (error) throw error;
      setMessage('¡Tu contraseña fue actualizada correctamente!');

      // After a short delay, send the user to login
      setTimeout(() => navigate('/auth'), 1200);
    } catch (err) {
      console.error('Error actualizando contraseña:', err);
      setError('No pudimos actualizar tu contraseña. Probá nuevamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-surface-bg flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="flex items-center justify-center mb-8">
          <img
            src="/images/estudio-atlas-logo-solo.svg"
            alt="Atlas logo"
            className="w-16 h-16"
          />
        </div>

        <div className="bg-surface-card rounded-lg shadow-md p-8">
          <h2 className="text-[17px] font-semibold text-brand-black/90 mb-2">Restablecer contraseña</h2>
          <p className="text-[15px] text-neutral-500 mb-6">
            {initializing
              ? 'Verificando enlace de restablecimiento...'
              : canReset
              ? 'Ingresá tu nueva contraseña para continuar.'
              : 'El enlace no es válido o está vencido.'}
          </p>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-sm text-red-700 text-[13px] flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {message && (
            <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-sm text-green-700 text-[13px] flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{message}</span>
            </div>
          )}

          {initializing ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-6 h-6 text-brand-primary animate-spin" />
            </div>
          ) : canReset ? (
            <form onSubmit={handleUpdatePassword} className="space-y-4">
              <div>
                <label htmlFor="password" className="block text-[13px] font-medium text-brand-black/70 mb-1.5">
                  Nueva contraseña
                </label>
                <div className="relative">
                  <input
                    id="password"
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full pl-10 pr-4 h-11 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
                <p className="text-[13px] text-neutral-500 mt-1">Mínimo 8 caracteres.</p>
              </div>

              <div>
                <label htmlFor="confirm" className="block text-[13px] font-medium text-brand-black/70 mb-1.5">
                  Confirmar contraseña
                </label>
                <div className="relative">
                  <input
                    id="confirm"
                    type="password"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full pl-10 pr-4 h-11 border border-neutral-200 rounded-md text-[15px] text-brand-black/90 placeholder:text-neutral-400 focus:ring-2 focus:ring-brand-primary/30 focus:border-brand-primary transition-colors"
                    placeholder="••••••••"
                    required
                  />
                  <Lock className="w-4 h-4 text-neutral-400 absolute left-3 top-1/2 -translate-y-1/2" />
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white h-11 px-4 rounded-md shadow-sm text-[15px] font-medium flex items-center justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <Loader2 className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    Guardar nueva contraseña
                    <CheckCircle className="w-5 h-5" />
                  </>
                )}
              </button>

              <div className="text-center text-[13px]">
                <button
                  type="button"
                  onClick={() => navigate('/auth')}
                  className="text-brand-primary hover:text-brand-primary-hover transition-colors"
                >
                  Volver a iniciar sesión
                </button>
              </div>
            </form>
          ) : (
            <div className="space-y-4">
              <button
                type="button"
                onClick={() => navigate('/auth')}
                className="w-full bg-brand-primary hover:bg-brand-primary-hover text-white h-11 px-4 rounded-md shadow-sm text-[15px] font-medium flex items-center justify-center transition-colors"
              >
                Solicitar un nuevo enlace
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
